import { useSessionContext } from '@shared/hooks'
import { AppNames } from '@shared/types'
import { getTokenFromUrl } from '@shared/utils'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Makes sure the session describes the CRM instance being opened.
 *
 * CRM is the only app that gates its menu and routes on
 * `current_application.application_role`, and the backend derives that field
 * from the `Saas-App-Token` header — which the request interceptor reads off the
 * URL. So the role depends on where the browser was when the session was
 * fetched.
 *
 * After a fresh login it was nowhere useful: the login flow awaits the session
 * while still on `/login` and only then navigates, so the response carries no
 * current application. Nothing corrects it later either, because the session
 * query is enabled from a token read once at module load — `null` for anyone who
 * just logged out — leaving the query disabled and never automatically
 * refetched. The role stayed missing, and its menu entries with it, until a
 * manual reload re-read everything with the CRM token in the URL.
 *
 * Requesting the session once per app token closes that gap: the request goes
 * out while the CRM token is in the URL, so the response describes this app.
 * A manual refetch runs even while the query is disabled, which is what the
 * login flow itself depends on.
 */
export const useAppSessionSync = () => {
  const { pathname } = useLocation()
  const { user, updateUserData } = useSessionContext()

  // Tracked by token rather than by session contents, so that the response can
  // never feed back into the condition that asked for it — no request loop,
  // whatever the backend answers.
  const syncedToken = useRef<string>()

  const isCrmOpen = pathname.startsWith(`/apps/${AppNames.Crm}`)
  const appToken = getTokenFromUrl()

  useEffect(() => {
    if (!isCrmOpen || !user || !appToken) return
    if (syncedToken.current === appToken) return

    syncedToken.current = appToken

    // Already the right instance — the case after a page reload, where the
    // session was fetched with this token in the URL to begin with.
    if (user.current_application?.token === appToken) return

    updateUserData()
  }, [isCrmOpen, user, appToken, updateUserData])
}
